import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronUpIcon, ChevronDownIcon, CalendarIcon } from '@radix-ui/react-icons';
import api from '../services/api';
import config from '../config/config';
import { formatDate } from '../utils/dateUtils';
import DynamicTable from '../components/Documents/DynamicTable';

// Dynamic column and row interfaces based on API response
interface Column {
  class: string;
  header: string;
  key: string;
  show: number;
}

interface Row {
  [key: string]: any;
}

interface ApiResponse {
  columns: Column[];
  rows: Row[];
  document: {
    id: string;
    name: string;
    type: string;
    status: string;
    upload_date: string;
    page_count: number;
    presigned_url: string;
  };
}

const BankStatementViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState<string>('');
  const [pageSortDirection, setPageSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [dateSortDirection, setDateSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [sortedRows, setSortedRows] = useState<Row[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Sort function for page numbers
  const sortByPageNumber = (direction: 'asc' | 'desc') => {
    if (!apiData?.rows) return;
    
    const sorted = [...apiData.rows].sort((a, b) => {
      const aPage = a.page_number || 0;
      const bPage = b.page_number || 0;
      
      if (direction === 'asc') {
        return aPage - bPage;
      } else {
        return bPage - aPage;
      }
    });
    
    setSortedRows(sorted);
    setPageSortDirection(direction);
    setDateSortDirection(null); // Clear date sort when sorting by page
  };

  // Sort function for dates
  const sortByDate = (direction: 'asc' | 'desc') => {
    if (!apiData?.rows) return;
    
    const sorted = [...apiData.rows].sort((a, b) => {
      // Parse dates in DD-MM-YYYY format
      const parseDate = (dateStr: string) => {
        if (!dateStr) return new Date(0);
        const [day, month, year] = dateStr.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      };
      
      const aDate = parseDate(a.date);
      const bDate = parseDate(b.date);
      
      if (direction === 'asc') {
        return aDate.getTime() - bDate.getTime();
      } else {
        return bDate.getTime() - aDate.getTime();
      }
    });
    
    setSortedRows(sorted);
    setDateSortDirection(direction);
    setPageSortDirection(null); // Clear page sort when sorting by date
  };

  // Filter by date range
  const filterByDateRange = () => {
    if (!apiData?.rows) return;
    
    let filtered = [...apiData.rows];
    
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(row => {
        if (!row.date) return false;
        
        const parseDate = (dateStr: string) => {
          const [day, month, year] = dateStr.split('-');
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        };
        
        const rowDate = parseDate(row.date);
        
        if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          if (rowDate < startDate) return false;
        }
        
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          if (rowDate > endDate) return false;
        }
        
        return true;
      });
    }
    
    setSortedRows(filtered);
  };

  // Clear date range filter
  const clearDateFilter = () => {
    setDateRange({ start: '', end: '' });
    if (apiData?.rows) {
      setSortedRows(apiData.rows);
    }
  };

  // Reset all sorts and filters
  const resetAllFilters = () => {
    if (apiData?.rows) {
      setSortedRows(apiData.rows);
    }
    setPageSortDirection(null);
    setDateSortDirection(null);
    setDateRange({ start: '', end: '' });
  };

  const handleExportExcel = async () => {
    if (!id) return;
    
    try {
      // Prepare request data with sort and filter parameters
      const requestData: any = {
        document_id: id
      };
      
      // Add sort parameters
      if (pageSortDirection) {
        requestData.sort_by = 'page_number';
        requestData.sort_direction = pageSortDirection;
      } else if (dateSortDirection) {
        requestData.sort_by = 'date';
        requestData.sort_direction = dateSortDirection;
      }
      
      // Add date range filter parameters
      if (dateRange.start) {
        requestData.date_start = dateRange.start;
      }
      if (dateRange.end) {
        requestData.date_end = dateRange.end;
      }
      
      // Call the backend API to download Excel file
      const response = await api.post(`${config.apiBaseUrl}/data/download-bank-statement`, requestData, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `bank_statement_${apiData?.document.name.replace(/\.[^/.]+$/, '')}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  useEffect(() => {
    const fetchBankStatementData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch bank statement details with dynamic columns
        const response = await api.get(`${config.apiBaseUrl}/data/bank-statement-details/${id}`);
        setApiData(response.data);
        
        // Initialize sorted rows with the fetched data
        if (response.data.rows) {
          setSortedRows(response.data.rows);
        }
        
        // Get presigned URL from the document data in the response
        if (response.data.document && response.data.document.presigned_url) {
          setPresignedUrl(response.data.document.presigned_url);
        }
      } catch (error) {
        console.error('Error fetching bank statement data:', error);
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBankStatementData();
  }, [id]);

  // Handle document click (no drill-down for bank statement)
  const handleDocumentClick = (rowId: string, type: string) => {
    // No drill-down functionality for bank statement - do nothing
  };

  // Format currency
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !apiData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-600 dark:text-red-400 mb-4">{error || 'No data available'}</div>
        <button
          onClick={() => navigate(-1)}
          className="text-primary hover:underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { document } = apiData;
  
  if (document.status === 'Processing') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-semibold">Processing Document...</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={() => navigate('/documents')}
              className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              View All Documents
            </button>
          </div>
        </div>

        <div className="flex gap-4">
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'} flex items-center justify-center p-8`}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Processing your document. This may take a few minutes...</p>
            </div>
          </div>

          {showPreview && (
            <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="h-full">
                <iframe
                  src={presignedUrl}
                  className="w-full h-[calc(100vh-12rem)]"
                  title="PDF Preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-semibold">
            {apiData.rows && apiData.rows.length > 0 
              ? `Bank Statement Entries - ${document.name}`
              : 'Bank Statement Document'
            }
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          {/* Page Sort Controls */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by Page:</span>
            <button
              onClick={() => sortByPageNumber('asc')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                pageSortDirection === 'asc'
                  ? 'bg-primary text-white dark:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <ChevronUpIcon className="w-3 h-3" />
              <span>Asc</span>
            </button>
            <button
              onClick={() => sortByPageNumber('desc')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                pageSortDirection === 'desc'
                  ? 'bg-primary text-white dark:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <ChevronDownIcon className="w-3 h-3" />
              <span>Desc</span>
            </button>
          </div>

          {/* Date Sort Controls */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by Date:</span>
            <button
              onClick={() => sortByDate('asc')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                dateSortDirection === 'asc'
                  ? 'bg-primary text-white dark:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <ChevronUpIcon className="w-3 h-3" />
              <span>Oldest</span>
            </button>
            <button
              onClick={() => sortByDate('desc')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                dateSortDirection === 'desc'
                  ? 'bg-primary text-white dark:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <ChevronDownIcon className="w-3 h-3" />
              <span>Newest</span>
            </button>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                showDateFilter || dateRange.start || dateRange.end
                  ? 'bg-primary text-white dark:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <CalendarIcon className="w-3 h-3" />
              <span>Date Range</span>
            </button>
            {(pageSortDirection || dateSortDirection || dateRange.start || dateRange.end) && (
              <button
                onClick={resetAllFilters}
                className="px-2 py-1 rounded-md text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
              >
                Reset All
              </button>
            )}
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleExportExcel}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            Export to Excel
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            View All Documents
          </button>
        </div>
      </div>

      {/* Date Range Filter Panel */}
      {showDateFilter && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                From:
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                To:
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <button
              onClick={filterByDateRange}
              className="px-3 py-1 bg-primary text-white rounded-md text-sm hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              Apply Filter
            </button>
            <button
              onClick={clearDateFilter}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4">
        {/* Table Section */}
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'}`}>
          <div className="p-6">
            {apiData.columns && sortedRows && (
              <DynamicTable 
                columns={apiData.columns}
                rows={sortedRows}
                selectedDocuments={[]}
                onSelectDocument={() => {}}
                onSelectAll={() => {}}
                onDocumentClick={handleDocumentClick}
                isRowExpanded={() => false}
                renderDrillDownContent={() => null}
                onDownloadExcel={() => {}}
                showDrillDown={false}
                showCheckboxes={false}
              />
            )}
          </div>
        </div>

        {/* PDF Preview Section */}
        {showPreview && (
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="h-full">
              <iframe
                src={presignedUrl}
                className="w-full h-[calc(100vh-12rem)]"
                title="PDF Preview"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankStatementViewPage;
